/**
 * Which guide fields the CMS fills in for itself, and when it stops.
 *
 * The rule this file encodes: a derived field keeps tracking its source for as
 * long as nobody has disagreed with it. The moment an editor types something
 * else, the CMS stops writing that field for the rest of the session, and says
 * so in the form. Nothing here is hidden and nothing here is final — every
 * value lands in an ordinary editable field.
 *
 * Kept free of React so the cascade can be tested as a function.
 */

import type { Guide } from "@zennopay/schema";

import {
    canonicalUrlFor,
    deriveExcerpt,
    deriveKeywords,
    deriveMetaDescription,
    deriveQuestionFromTitle,
    deriveSeoTitle,
    readingTimeMinutes,
    slugify
} from "./text";

export type GuideValues = Partial<Omit<Guide, "id">>;

export type DerivableKey =
    | "slug"
    | "canonicalUrl"
    | "readingTime"
    | "primaryQuestion"
    | "excerpt"
    | "seoTitle"
    | "metaDescription"
    | "primaryKeyword"
    | "secondaryKeywords";

export interface DerivedFieldSpec {
    key: DerivableKey;
    /** Named in the editor's words, for the "filled in for you" summary. */
    label: string;
    /** What it was worked out from, so the derivation is never a black box. */
    from: string;
    /** `undefined` means "not enough input yet"; the field is left alone. */
    derive: (values: GuideValues, origin: string) => unknown;
    /** A reason this field must not be auto-updated, or undefined. */
    lockedBecause?: (values: GuideValues) => string | undefined;
}

/**
 * Order matters: `canonicalUrl` reads the slug this same pass produced.
 *
 * Deriving each field from the running result rather than from the form's
 * committed values is what makes a single keystroke in the title propagate all
 * the way to the canonical URL without a render in between.
 */
export const DERIVED_FIELDS: DerivedFieldSpec[] = [
    {
        key: "slug",
        label: "Slug",
        from: "the title",
        derive: (values) => slugify(values.title ?? "") || undefined,
        /* Changing a published slug breaks a live URL and needs a 301. The gate
           flags the collision; this stops the CMS causing it in the first
           place. An editor can still change it by hand, deliberately. */
        lockedBecause: (values) =>
            values.status === "published"
                ? "This guide is published. Its URL is live, so the slug is no longer updated from the title; changing it needs a 301 redirect."
                : undefined
    },
    {
        key: "canonicalUrl",
        label: "Canonical URL",
        from: "the slug",
        derive: (values, origin) => canonicalUrlFor(values.slug ?? "", origin) || undefined
    },
    {
        key: "readingTime",
        label: "Reading time",
        from: "the body word count",
        derive: (values) => readingTimeMinutes(values.body ?? "") || undefined
    },
    /* Before the excerpt and the meta description, both of which read what this
       pass produces rather than what the form last committed. */
    {
        key: "primaryQuestion",
        label: "Primary question",
        from: "the title",
        derive: (values) => deriveQuestionFromTitle(values.title ?? "") || undefined
    },
    {
        key: "excerpt",
        label: "Excerpt",
        from: "the direct answer",
        derive: (values) => deriveExcerpt(values.directAnswer ?? "", values.body ?? "") || undefined
    },
    {
        key: "seoTitle",
        label: "SEO title",
        from: "the title",
        derive: (values) => deriveSeoTitle(values.title ?? "") || undefined
    },
    {
        key: "metaDescription",
        label: "Meta description",
        from: "the excerpt",
        derive: (values) =>
            deriveMetaDescription(values.excerpt ?? "", values.directAnswer ?? "") || undefined
    },
    {
        key: "primaryKeyword",
        label: "Primary keyword",
        from: "the words the title and body use most",
        derive: (values) => deriveKeywords(values.title ?? "", values.body ?? "").primary
    },
    {
        key: "secondaryKeywords",
        label: "Secondary keywords",
        from: "the words the title and body use most",
        derive: (values) => {
            const { secondary } = deriveKeywords(values.title ?? "", values.body ?? "");
            return secondary.length > 0 ? secondary : undefined;
        }
    }
];

export const DERIVED_FIELD_BY_KEY: Record<DerivableKey, DerivedFieldSpec> = Object.fromEntries(
    DERIVED_FIELDS.map((field) => [field.key, field])
) as Record<DerivableKey, DerivedFieldSpec>;

export const isEmptyValue = (value: unknown): boolean =>
    value === undefined ||
    value === null ||
    (typeof value === "string" && value.trim() === "") ||
    (Array.isArray(value) && value.length === 0);

/** Structural equality, because `secondaryKeywords` is an array. */
export const sameValue = (a: unknown, b: unknown): boolean =>
    a === b || JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * The fields still tracking their source when a guide is opened.
 *
 * A field counts as tracking if it is empty, or if what is stored is exactly
 * what would be derived now. The second case is what lets reading time keep
 * following the body across sessions: nothing is persisted to say "this was
 * automatic", and the value itself is the evidence.
 *
 * A stored value that disagrees is an override, and overrides survive.
 */
export function trackingFields(values: GuideValues, origin: string): Set<DerivableKey> {
    const tracking = new Set<DerivableKey>();
    for (const field of DERIVED_FIELDS) {
        if (field.lockedBecause?.(values)) continue;
        const current = values[field.key];
        if (isEmptyValue(current) || sameValue(current, field.derive(values, origin))) {
            tracking.add(field.key);
        }
    }
    return tracking;
}

export interface DerivationResult {
    /** Only the fields whose value should change. Empty when nothing moved. */
    changes: Partial<Record<DerivableKey, unknown>>;
    /** Every derived value, tracking or not, for display. */
    resolved: Partial<Record<DerivableKey, unknown>>;
}

/**
 * One pass of the cascade over the current form values.
 *
 * `resolved` is computed for every field, including overridden ones, so the UI
 * can offer "reset to derived" without recomputing. `changes` is the subset a
 * tracking field would actually move to.
 */
export function deriveValues(
    values: GuideValues,
    tracking: Set<DerivableKey>,
    origin: string
): DerivationResult {
    const running: GuideValues = { ...values };
    const changes: Partial<Record<DerivableKey, unknown>> = {};
    const resolved: Partial<Record<DerivableKey, unknown>> = {};

    for (const field of DERIVED_FIELDS) {
        const derived = field.derive(running, origin);
        resolved[field.key] = derived;

        const locked = Boolean(field.lockedBecause?.(values));
        if (locked || !tracking.has(field.key) || derived === undefined) continue;
        if (sameValue(running[field.key], derived)) continue;

        changes[field.key] = derived;
        (running as Record<string, unknown>)[field.key] = derived;
    }

    return { changes, resolved };
}

/**
 * Fills in every derivable field that is still empty.
 *
 * The live form is the main path, but a field inside a collapsed panel is never
 * mounted and therefore never runs, and an import or a copy never opens a form
 * at all. This is the backstop, and it only ever writes into a gap: a value an
 * editor put there is left exactly as it is.
 */
/* How long a published guide runs before it is worth re-reading. Applied at
   publication, not at creation, because the clock only means anything once the
   page is live. Always overridable. */
export const REVIEW_INTERVAL_MONTHS = 6;

export function monthsFrom(iso: string, months: number): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    date.setUTCMonth(date.getUTCMonth() + months);
    return date.toISOString();
}

/**
 * What publication fills in for itself: two dates and an identity.
 *
 * Shared between the collection's `onPreSave`, which actually writes them, and
 * the publish gate panel, which has to evaluate the guide as it *will be
 * saved*. Without that, every new guide reads as blocked on a publish date the
 * editor was never going to type.
 *
 * The publish date is the moment of publication rather than a guess made at
 * creation: a guide drafted in January and published in March is a March guide.
 *
 * `publishedBy` is the signed-in user, not a field. It is the answer to "who
 * put this live", and the only party that can answer it honestly is the session
 * doing the putting: a text box would record whoever typed into it. It is
 * overwritten rather than preserved on each crossing, because a guide taken
 * down and re-published was published by the person who re-published it, and
 * `guideRevisions` keeps the previous answer. `functions/src/publish.ts` reads
 * it as the actor for the audit log, and records "unknown" when it is absent —
 * which, before this, was every publish the CMS performed.
 */
export function withPublishDefaults<T extends GuideValues>(
    values: T,
    now: string,
    publishedBy?: string
): T {
    const publishedAt = values.publishedAt || now;
    const actor = (publishedBy ?? "").trim();
    return {
        ...values,
        publishedAt,
        nextReviewAt: values.nextReviewAt || monthsFrom(publishedAt, REVIEW_INTERVAL_MONTHS),
        ...(actor ? { publishedBy: actor } : {})
    };
}

export function fillEmptyDerivable<T extends GuideValues>(values: T, origin: string): T {
    const running: GuideValues = { ...values };
    const filled: Record<string, unknown> = { ...values };

    for (const field of DERIVED_FIELDS) {
        if (!isEmptyValue(running[field.key])) continue;
        const derived = field.derive(running, origin);
        if (derived === undefined) continue;
        filled[field.key] = derived;
        (running as Record<string, unknown>)[field.key] = derived;
    }

    return filled as T;
}
